import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { whenIdle } from '@/core/idleGate';

/**
 * ModelLoader.ts — the glTF asset pipeline for vehicle models (M35 Stage 2).
 *
 * The game shipped with ZERO external 3D assets (everything procedural), so this
 * is the first real asset loader. It parses each .glb ONCE and hands out clones,
 * so several racers can share one downloaded model cheaply.
 *
 * M150: the vehicle GLBs ship meshopt-compressed (EXT_meshopt_compression +
 * KHR_mesh_quantization, packed by `npm run models:pack`), so the loader
 * carries three's MeshoptDecoder (a ~20 kB wasm). Uncompressed files (the
 * landmarks) load exactly as before — the decoder only engages on files that
 * declare the extension.
 */
/** three names a glTF mesh that has no name of its own `mesh_<index>`, and
 *  `mesh_<index>_<n>` for the parts of a multi-primitive mesh. */
const AUTO_MESH_NAME = /^mesh_\d+(?:_\d+)*$/;

/**
 * M153: give every unnamed mesh the name of the node it hangs under.
 *
 * gltfpack (`npm run models:pack`, `-kn`) keeps a named node but moves its
 * quantized mesh onto an UNNAMED child node that carries the dequantization
 * transform, so the loaded scene reads Group "Tyre_FL" -> Mesh "mesh_4" where
 * the raw export reads Mesh "Tyre_FL". Every consumer of the wheel contract
 * (CarView.spinifyWheels, the garage close-up's collectWheels, the painted
 * wheel face, the barrel/disc paint branch) matches the MESH name, as the raw
 * export has it, so on the packed fleet the wheels did not roll and the
 * Dragon Claws never got their face (measured in the lab: no mesh under a
 * dressed pickup was named Tyre_*). Restoring the raw shape here, once per
 * parsed scene before the first clone, is the one fix for all of them.
 * Named meshes are left alone (the unpacked landmarks already carry their
 * node's name); unnamed non-mesh intermediates (a multi-primitive mesh's
 * group) are walked through. tests/render/modelNames.test.ts pins it on the
 * REAL packed fleet.
 */
export function hoistNodeNames<T extends THREE.Object3D>(root: T): T {
  const walk = (o: THREE.Object3D, name: string): void => {
    for (const c of o.children) {
      if (c.name && !AUTO_MESH_NAME.test(c.name)) continue; // a named child owns its subtree
      if ((c as THREE.Mesh).isMesh) c.name = name;
      walk(c, name);
    }
  };
  root.traverse((n) => {
    if (n === root || (n as THREE.Mesh).isMesh) return;
    if (n.name && !AUTO_MESH_NAME.test(n.name)) walk(n, n.name);
  });
  return root;
}

export class ModelLoader {
  private readonly loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
  /** url → the parsed source scene (cloned on each load). */
  private readonly cache = new Map<string, Promise<THREE.Group>>();

  /** Load a .glb and resolve a fresh CLONE of its scene graph (safe to mutate).
   *  Loading round 2: the bytes are fetched first, then PARSED (the
   *  main-thread cost — geometry + material construction, 10-300 ms per
   *  model) only once the player is quiet (idleGate), so a burst of landmark
   *  models arriving right after the title never lands under a tap. A
   *  foreground job (a race launch) bypasses the wait. */
  async load(url: string): Promise<THREE.Group> {
    let src = this.cache.get(url);
    if (!src) {
      src = this.fetchAndParse(url).then((gltf) => hoistNodeNames(gltf.scene));
      this.cache.set(url, src);
    }
    return (await src).clone(true);
  }

  private async fetchAndParse(url: string): Promise<GLTF> {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`ModelLoader: ${url} → HTTP ${res.status}`);
    const bytes = await res.arrayBuffer();
    // Quiet-gated but NEVER held by a live race: a model the race needs must
    // land within the cap even mid-drive (a brief stall beats a missing landmark).
    await whenIdle(200, 2500, { holdDuringRace: false });
    const base = url.slice(0, url.lastIndexOf('/') + 1);
    return new Promise<GLTF>((resolve, reject) => {
      this.loader.parse(bytes, base, resolve, reject);
    });
  }

  /**
   * Normalise a freshly-loaded model to the game's conventions: recentre on its
   * own footprint, drop it onto the ground (y=0 at the tyres), and scale so its
   * LENGTH matches `targetLength` world units. Returns the wrapper group to add
   * to a CarView. Model-agnostic, so any clean vehicle GLB fits the physics box
   * without per-asset tweaking. `forwardIsZ` flips models authored facing -Z.
   */
  static fit(model: THREE.Group, targetLength: number, forwardIsZ = true): THREE.Group {
    const box = new THREE.Box3().setFromObject(model);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);

    // Scale by the longer horizontal axis = the car's length.
    const length = Math.max(size.x, size.z);
    const scale = length > 1e-3 ? targetLength / length : 1;

    const wrap = new THREE.Group();
    model.position.set(-center.x, -box.min.y, -center.z); // centre X/Z, feet on y=0
    model.scale.setScalar(scale);
    model.position.multiplyScalar(scale);
    // If the car's length lies along world X, yaw it so length runs along Z (our
    // forward axis, §6.1). If it's already along Z, leave it.
    if (size.x > size.z) wrap.rotation.y = Math.PI / 2;
    if (!forwardIsZ) wrap.rotation.y += Math.PI;

    model.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        m.castShadow = true;
        m.receiveShadow = false;
      }
    });
    wrap.add(model);
    return wrap;
  }
}

export const modelLoader = new ModelLoader();
